import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Wrench, RotateCcw, CircleDot, Wind, Settings, Gauge, Link2 } from 'lucide-react';

const SERVICES = [
  {
    id: 'acil-lastik-tamiri',
    icon: Wrench,
    title: 'Acil Lastik Tamiri',
    desc: 'Bulunduğunuz yerde, aracınızın başında profesyonel tamir. Anında.',
    color: 'rgba(229,51,51,.12)',
    borderColor: 'rgba(229,51,51,.25)',
    iconColor: 'var(--red)',
  },
  {
    id: 'lastik-degisimi',
    icon: RotateCcw,
    title: 'Lastik Değişimi',
    desc: 'Servise gitmeyin. Yeni lastik montajı için biz size geliyoruz.',
    color: 'rgba(74,143,245,.12)',
    borderColor: 'rgba(74,143,245,.25)',
    iconColor: 'var(--blue)',
  },
  {
    id: 'stepne-hizmeti',
    icon: CircleDot,
    title: 'Stepne Hizmeti',
    desc: 'Stepneniz yoksa, yerine anında yeni lastik takıyoruz.',
    color: 'rgba(16,185,129,.12)',
    borderColor: 'rgba(16,185,129,.25)',
    iconColor: 'var(--green)',
  },
  {
    id: 'sibop-tamiri',
    icon: Wind,
    title: 'Sibop Tamiri',
    desc: 'Sibop arızasını yerinde gideriyor, lastiğinizi şişiriyoruz.',
    color: 'rgba(255,175,50,.1)',
    borderColor: 'rgba(255,175,50,.25)',
    iconColor: '#ff9500',
  },
  {
    id: 'jant-tamiri',
    icon: Settings,
    title: 'Jant Problemleri',
    desc: 'Her tip jant arızasına anlık çözüm. Profesyonel ekip.',
    color: 'rgba(147,51,234,.12)',
    borderColor: 'rgba(147,51,234,.25)',
    iconColor: '#9333ea',
  },
  {
    id: 'hava-basma',
    icon: Gauge,
    title: 'Hava Basınç Ayarı',
    desc: 'Lastiğinizin basıncını ideal seviyeye getiriyoruz.',
    color: 'rgba(236,72,153,.12)',
    borderColor: 'rgba(236,72,153,.25)',
    iconColor: '#ec4899',
  },
  {
    id: 'zincir-takma',
    icon: Link2,
    title: 'Zincir Takma',
    desc: 'Kış koşullarında güvenli seyahat için 7/24 aktif destek.',
    color: 'rgba(59,130,246,.12)',
    borderColor: 'rgba(59,130,246,.25)',
    iconColor: '#3b82f6',
  },
];

export default function Services() {
  return (
    <section
      data-testid="services-section"
      className="section"
      style={{
        background: 'var(--bg2)',
        borderTop: '1px solid var(--border)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient glow */}
      <div
        style={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 700,
          height: 500,
          background: 'radial-gradient(ellipse, rgba(74,143,245,.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div className="wrap">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem', position: 'relative' }}>
          <div className="pill" style={{ marginBottom: '1.125rem' }}>
            Hizmetlerimiz
          </div>
          <h2 className="h2" style={{ marginBottom: '.875rem' }}>
            Yolda Kaldığınızda <span style={{ color: 'var(--blue)' }}>Yanınızdayız</span>
          </h2>
          <p className="body" style={{ maxWidth: 520, margin: '0 auto', color: 'var(--text2)' }}>
            Her araç tipi, her lastik sorunu. Servisimize gelmenize gerek yok — biz size geliyoruz.
          </p>
        </div>

        {/* Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.25rem',
          }}
        >
          {SERVICES.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <motion.div
                key={svc.id}
                data-testid={`services-card-${svc.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: .5, delay: i * .06 }}
                style={{
                  background: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--rl)',
                  padding: '2rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.125rem',
                  cursor: 'default',
                  transition: 'all .25s',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = svc.borderColor;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Glow on hover */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: `radial-gradient(ellipse at top left, ${svc.color} 0%, transparent 60%)`,
                    opacity: 0,
                    transition: 'opacity .3s',
                    pointerEvents: 'none',
                  }}
                  className="svc-glow"
                />

                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 13,
                    background: svc.color,
                    border: `1px solid ${svc.borderColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: svc.iconColor,
                    transition: 'all .3s',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Icon size={22} strokeWidth={1.75} />
                </div>

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <h3
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      color: 'var(--text)',
                      marginBottom: '.5rem',
                    }}
                  >
                    {svc.title}
                  </h3>
                  <p
                    style={{
                      fontSize: '.875rem',
                      color: 'var(--text2)',
                      lineHeight: 1.7,
                    }}
                  >
                    {svc.desc}
                  </p>
                </div>

                <Link
                  to="/acil-yol-yardim"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '.35rem',
                    color: svc.iconColor,
                    fontSize: '.85rem',
                    fontWeight: 600,
                    marginTop: 'auto',
                    transition: 'gap .2s',
                    position: 'relative',
                    zIndex: 1,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.gap = '.55rem')}
                  onMouseLeave={(e) => (e.currentTarget.style.gap = '.35rem')}
                >
                  Detaylar <ArrowRight size={14} />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      <style>{`
        [data-testid^="services-card"]:hover .svc-glow {
          opacity: 1 !important;
        }
      `}</style>
    </section>
  );
}
